// components/profile/sections/BasicInfoSection.jsx
import React from 'react';
import { FormField } from '../components/FormField'; 
import { useProfileFormContext } from '../context/ProfileFormContext';
import { 
  BuildingOfficeIcon,
  LightBulbIcon,
  PlusIcon,
  MinusIcon
} from '@heroicons/react/24/outline';

export const BasicInfoSection = () => {
  const {
    formData,
    updateField,
    setFieldTouched,
    errors,
    touched
  } = useProfileFormContext();

  const handlePriceIncrement = (increment) => {
    const currentValue = parseInt(formData.startingPrice) || 0;
    const newValue = Math.max(0, currentValue + increment);
    updateField('startingPrice', newValue.toString());
  };

  const handleWeeksIncrement = (increment) => {
    const currentValue = parseInt(formData.minimumTimeframe) || 1;
    const newValue = Math.max(1, Math.min(52, currentValue + increment));
    updateField('minimumTimeframe', newValue.toString());
  };

  // Add CSS to hide number input spinners
  React.useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type="number"] {
        -moz-appearance: textfield;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div className="card" style={{
      backgroundColor: 'var(--Surface-Primary)',
      borderRadius: 'var(--radius-xl)',
      padding: 'var(--spacing-l)',
      boxShadow: 'var(--shadow-frame-small)'
    }}>
      {/* Section Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 flex items-center justify-center" style={{
            background: 'var(--brand-gradient-2)',
            borderRadius: 'var(--radius-s)'
          }}>
            <BuildingOfficeIcon className="w-5 h-5" style={{ color: 'var(--Grey-800)' }} />
          </div>
          <h2 style={{
            fontSize: 'var(--font-size-h5)',
            fontWeight: 'var(--font-weight-h5)',
            color: 'var(--Text-Primary)',
            lineHeight: 'var(--line-height-h5)'
          }}>
            Basic Information
          </h2>
        </div>
        <p style={{ 
          color: 'var(--Text-Secondary)',
          fontSize: 'var(--font-size-body3)',
          marginLeft: '52px'
        }}>
          Customers will see this information on your Boutee profile. Add the essential details – from your brand name and contact information to the basics that give customers confidence when discovering you on Boutee.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Company Name"
          required
          error={touched.companyName && errors.companyName}
          helperText="Your business or brand name"
          tooltipText="This is how customers will identify your business on Boutee"
          hideCompletionIcon={true}
          labelStyle={{
            fontSize: 'var(--font-size-subtitle3)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Text-Primary)'
          }}
        >
          <input
            type="text"
            value={formData.companyName || ''}
            onChange={(e) => updateField('companyName', e.target.value)}
            onBlur={() => setFieldTouched('companyName')}
            className="form-input"
            placeholder="e.g., Smith & Co. Fine Jewellery"
            style={{
              width: '100%',
              padding: 'var(--spacing-s)',
              borderRadius: 'var(--radius-s)',
              border: '1px solid var(--Stroke-Secondary)',
              fontSize: 'var(--font-size-body3)'
            }}
          />
        </FormField>

        <FormField
          label="Location (Town/City)"
          required
          error={touched.location && errors.location}
          helperText="Primary location of your business"
          tooltipText="Helps customers find local jewellers near them"
          hideCompletionIcon={true}
          labelStyle={{
            fontSize: 'var(--font-size-subtitle3)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Text-Primary)'
          }}
        >
          <input
            type="text"
            value={formData.location || ''}
            onChange={(e) => updateField('location', e.target.value)}
            onBlur={() => setFieldTouched('location')}
            className="form-input"
            placeholder="e.g., London, Birmingham, Edinburgh"
            style={{
              width: '100%',
              padding: 'var(--spacing-s)',
              borderRadius: 'var(--radius-s)',
              border: '1px solid var(--Stroke-Secondary)',
              fontSize: 'var(--font-size-body3)'
            }}
          />
        </FormField>

        <FormField
          label="Business Email"
          required
          error={touched.email && errors.email}
          helperText="Where customers can reach you"
          tooltipText="This email will be visible to customers for enquiries"
          hideCompletionIcon={true}
          labelStyle={{
            fontSize: 'var(--font-size-subtitle3)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Text-Primary)'
          }}
        >
          <input
            type="email"
            value={formData.email || ''}
            onChange={(e) => updateField('email', e.target.value)}
            onBlur={() => setFieldTouched('email')}
            className="form-input"
            placeholder="contact@yourjewellery.com"
            style={{
              width: '100%',
              padding: 'var(--spacing-s)',
              borderRadius: 'var(--radius-s)',
              border: '1px solid var(--Stroke-Secondary)',
              fontSize: 'var(--font-size-body3)'
            }}
          />
        </FormField>

        <FormField
          label="Website"
          error={touched.website && errors.website}
          helperText="Optional. This will appear to other jewellers in the Community section if you add it."
          tooltipText="Add your full website link if you want other jewellers on Boutee to be able to visit your site."
          hideCompletionIcon={true}
          labelStyle={{
            fontSize: 'var(--font-size-subtitle3)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Text-Primary)'
          }}
        >
          <input
            type="url"
            value={formData.website || ''}
            onChange={(e) => updateField('website', e.target.value)}
            onBlur={() => setFieldTouched('website')}
            className="form-input"
            placeholder="https://www.yourjewellery.com"
            style={{
              width: '100%',
              padding: 'var(--spacing-s)',
              borderRadius: 'var(--radius-s)',
              border: '1px solid var(--Stroke-Secondary)',
              fontSize: 'var(--font-size-body3)'
            }}
          />
        </FormField>

        <FormField
          label="Instagram"
          error={touched.instagram && errors.instagram}
          helperText="Optional. Leave blank if you do not want this shown on your Community profile."
          tooltipText="Add your Instagram profile link so other jewellers can find your work through the Community section."
          hideCompletionIcon={true}
          labelStyle={{
            fontSize: 'var(--font-size-subtitle3)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Text-Primary)'
          }}
        >
          <input
            type="url"
            value={formData.instagram || ''}
            onChange={(e) => updateField('instagram', e.target.value)}
            onBlur={() => setFieldTouched('instagram')}
            className="form-input"
            placeholder="https://www.instagram.com/yourhandle"
            style={{
              width: '100%',
              padding: 'var(--spacing-s)',
              borderRadius: 'var(--radius-s)',
              border: '1px solid var(--Stroke-Secondary)',
              fontSize: 'var(--font-size-body3)'
            }}
          />
        </FormField>

        <FormField
          label="Starting Price"
          required
          error={touched.startingPrice && errors.startingPrice}
          helperText="Minimum budget for custom pieces (GBP)"
          tooltipText="Sets customer expectations for your price range"
          hideCompletionIcon={true}
          labelStyle={{
            fontSize: 'var(--font-size-subtitle3)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Text-Primary)'
          }}
        >
          <div className="relative flex items-center">
            <span style={{
              position: 'absolute',
              left: '12px',
              color: 'var(--Text-Secondary)',
              fontSize: 'var(--font-size-body3)',
              zIndex: 1,
              pointerEvents: 'none'
            }}>£</span>
            <input
              type="number"
              min="0"
              value={formData.startingPrice || ''}
              onChange={(e) => updateField('startingPrice', e.target.value)}
              onBlur={() => setFieldTouched('startingPrice')}
              className="form-input"
              placeholder="1000"
              style={{
                width: '100%',
                padding: 'var(--spacing-s)',
                paddingLeft: '32px',
                paddingRight: '80px',
                borderRadius: 'var(--radius-s)',
                border: '1px solid var(--Stroke-Secondary)',
                fontSize: 'var(--font-size-body3)'
              }}
            />
            <div className="absolute right-2 flex gap-1">
              <button
                type="button"
                onClick={() => handlePriceIncrement(-100)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: 'var(--radius-xs)',
                  border: '1px solid var(--Stroke-Secondary)',
                  backgroundColor: 'var(--Surface-Primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--Grey-100)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--Surface-Primary)';
                }}
              >
                <MinusIcon className="w-4 h-4" style={{ color: 'var(--Text-Secondary)' }} />
              </button>
              <button
                type="button"
                onClick={() => handlePriceIncrement(100)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: 'var(--radius-xs)',
                  border: '1px solid var(--Stroke-Secondary)',
                  backgroundColor: 'var(--Surface-Primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--Grey-100)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--Surface-Primary)';
                }}
              >
                <PlusIcon className="w-4 h-4" style={{ color: 'var(--Text-Secondary)' }} />
              </button>
            </div>
          </div>
        </FormField>

        <FormField
          label="Typical Timeframe"
          required
          error={touched.minimumTimeframe && errors.minimumTimeframe}
          helperText="Average production time in weeks"
          tooltipText="How long customers should expect to wait for their piece on average. Dont worry about this being too accurate - it's just to give customers a general idea of your production timescale."
          hideCompletionIcon={true}
          labelStyle={{
            fontSize: 'var(--font-size-subtitle3)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Text-Primary)'
          }}
        >
          <div className="relative flex items-center">
            <input
              type="number"
              min="1"
              max="52"
              value={formData.minimumTimeframe || ''}
              onChange={(e) => updateField('minimumTimeframe', e.target.value)}
              onBlur={() => setFieldTouched('minimumTimeframe')}
              className="form-input"
              placeholder="4"
              style={{
                width: '100%',
                padding: 'var(--spacing-s)',
                paddingRight: '140px',
                borderRadius: 'var(--radius-s)',
                border: '1px solid var(--Stroke-Secondary)',
                fontSize: 'var(--font-size-body3)'
              }}
            />
            <span style={{
              position: 'absolute',
              right: '80px',
              color: 'var(--Text-Secondary)',
              fontSize: 'var(--font-size-body4)',
              pointerEvents: 'none'
            }}>
              weeks
            </span>
            <div className="absolute right-2 flex gap-1">
              <button
                type="button"
                onClick={() => handleWeeksIncrement(-1)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: 'var(--radius-xs)',
                  border: '1px solid var(--Stroke-Secondary)',
                  backgroundColor: 'var(--Surface-Primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--Grey-100)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--Surface-Primary)';
                }}
              >
                <MinusIcon className="w-4 h-4" style={{ color: 'var(--Text-Secondary)' }} />
              </button>
              <button
                type="button"
                onClick={() => handleWeeksIncrement(1)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: 'var(--radius-xs)',
                  border: '1px solid var(--Stroke-Secondary)',
                  backgroundColor: 'var(--Surface-Primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--Grey-100)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--Surface-Primary)';
                }}
              >
                <PlusIcon className="w-4 h-4" style={{ color: 'var(--Text-Secondary)' }} />
              </button>
            </div>
          </div>
        </FormField>

        <div className="md:col-span-2">
          <FormField
            label="Business Introduction"
            required
            error={touched.introParagraph && errors.introParagraph}
            helperText={`Tell customers about your business. Make it engaging and informative. (${formData.introParagraph?.length || 0}/500 characters)`}
            tooltipText="This is the first thing customers will read about your business."
            hideCompletionIcon={true}
            labelStyle={{
              fontSize: 'var(--font-size-subtitle3)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--Text-Primary)'
            }}
          >
            <textarea
              value={formData.introParagraph || ''}
              onChange={(e) => updateField('introParagraph', e.target.value)}
              onBlur={() => setFieldTouched('introParagraph')}
              className="form-textarea"
              rows={5}
              maxLength={500}
              placeholder="Share your story, what makes your jewellery unique, and what customers can expect when working with you..."
              style={{
                width: '100%',
                padding: 'var(--spacing-s)',
                borderRadius: 'var(--radius-s)',
                border: '1px solid var(--Stroke-Secondary)',
                fontSize: 'var(--font-size-body3)',
                resize: 'vertical',
                minHeight: '120px'
              }}
            />
          </FormField>
        </div>
      </div>

      {/* Combined Tips Section */}
      <div className="mt-8" style={{
        background: 'var(--brand-gradient-2)',
        borderRadius: 'var(--radius-m)',
        padding: 'var(--spacing-m)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--spacing-s)'
      }}>
        <span style={{ fontSize: '25px', marginTop: '0px' }}>💡</span>
        <div>
          <h6 style={{
            fontSize: 'var(--font-size-h6)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--Text-Primary)',
            marginBottom: 'var(--spacing-xxs)',
            margin: 0
          }}>
            Tips for this section
          </h6>
          <p style={{
            fontSize: 'var(--font-size-body4)',
            color: 'var(--Grey-900)',
            margin: 0,
            marginTop: 'var(--spacing-xxs)',
            lineHeight: '2'
          }}>
          </p>
          <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
            • Feel free to update your profile as you wish - keep it fresh!
          </p>
          <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
            • Your introduction is crucial - make it personal and engaging.
          </p>
          <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
            • Use your official business name as registered.
          </p>
          <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
            • Be realistic about timeframes - customers prefer accuracy over speed.
          </p>
        </div>
      </div>
    </div>
  );
};
